import { getDepositsConfig, getFundsBalances } from '@/api/funds';
import {
  addDecimalStrings,
  minUnitToUsdt,
  minUnitToWatt,
  WATT_DECIMALS,
  USDT_DECIMALS,
} from '@/common/utils/money';

export interface Asset {
  chain_id: number;
  token_symbol: string;
  token_address: string | null;
  balance: string;
  frozen_balance: string;
  available_balance: string;
  balance_usdt: string;
  icon: string;
  current_price: number;
  price_change_24h: number;
  /** USDT 人工复核限制（§7）；仅 USDT 资产有意义 */
  restricted?: boolean;
}

export interface AssetsData {
  total_balance_usdt: string;
  assets: Asset[];
  /** USDT 受限时不可兑换 */
  usdtRestricted?: boolean;
}

function safeMinUnitToWatt(value: string | number | undefined, decimals?: number | null): string {
  try {
    return minUnitToWatt(String(value ?? '0'), decimals);
  } catch {
    return '0';
  }
}

function safeMinUnitToUsdt(value: string | number | undefined, decimals?: number | null): string {
  try {
    return minUnitToUsdt(String(value ?? '0'), decimals);
  } catch {
    return '0';
  }
}

function safeAdd(...parts: string[]): string {
  return parts.reduce((acc, cur) => {
    try {
      return addDecimalStrings(acc, cur || '0');
    } catch {
      return acc;
    }
  }, '0');
}

/**
 * deposits/config.exchangeRate：numerator/denominator = 每 1 USDT 对应的 JOULE 数量。
 * 展示「1 JOULE = X USDT」时 X = denominator / numerator。
 * 例：numerator=2, denominator=1 → 1 JOULE = 0.5 USDT。
 */
export function usdtPerJouleFromExchangeRate(
  rate: { numerator?: string; denominator?: string } | null | undefined
): number {
  if (!rate) return 1;
  try {
    const num = BigInt(String(rate.numerator ?? '').trim());
    const den = BigInt(String(rate.denominator ?? '').trim());
    if (num <= 0n || den < 0n) return 1;
    // 用固定小数避免 Number 直接除大整数；展示价通常很小
    const scale = 1_000_000_000n;
    const scaled = (den * scale) / num;
    const whole = scaled / scale;
    const frac = scaled % scale;
    const asNumber = Number(whole) + Number(frac) / Number(scale);
    return Number.isFinite(asNumber) && asNumber >= 0 ? asNumber : 1;
  } catch {
    return 1;
  }
}

/** 人类可读金额 × 汇率 → USDT 字符串（仅展示用） */
function humanTimesRate(amount: string, rate: number): string {
  const n = parseFloat(String(amount || '0'));
  if (!Number.isFinite(n) || !Number.isFinite(rate) || rate < 0) return '0';
  const product = n * rate;
  if (!Number.isFinite(product)) return '0';
  return product.toFixed(8).replace(/\.?0+$/, '') || '0';
}

/**
 * GET /v1/funds/balances → AssetsData
 * available/frozen 为最小单位整数串，必须按 decimals 换算后再展示。
 * Primary platform symbol is WATT; TAKER alias kept for legacy page lookups.
 * JOULE→USDT 展示价来自 GET /v1/deposits/config.exchangeRate。
 * 总资产展示按「平台币按汇率折 U + USDT」；禁止把同一笔余额加两次，禁止 Number 浮点求和。
 */
export const getAssets = async (): Promise<AssetsData> => {
  const [balances, depositCfg] = await Promise.all([
    getFundsBalances(),
    getDepositsConfig().catch(() => null),
  ]);
  const platform = balances.platform;
  const usdt = balances.usdt;
  const jouleUsdtPrice = usdtPerJouleFromExchangeRate(depositCfg?.exchangeRate);

  const wattDecimals = platform?.decimals ?? WATT_DECIMALS;
  const usdtDecimals = usdt?.decimals ?? USDT_DECIMALS;

  const wattAvailable = safeMinUnitToWatt(platform?.available, wattDecimals);
  const wattFrozen = safeMinUnitToWatt(platform?.frozen, wattDecimals);
  const wattBalance = (() => {
    try {
      const a = BigInt(String(platform?.available ?? '0'));
      const f = BigInt(String(platform?.frozen ?? '0'));
      return minUnitToWatt((a + f).toString(), wattDecimals);
    } catch {
      return wattAvailable;
    }
  })();

  const usdtAvailable = safeMinUnitToUsdt(usdt?.available, usdtDecimals);
  const usdtFrozen = safeMinUnitToUsdt(usdt?.frozen, usdtDecimals);
  const usdtBalance = (() => {
    try {
      const a = BigInt(String(usdt?.available ?? '0'));
      const f = BigInt(String(usdt?.frozen ?? '0'));
      return minUnitToUsdt((a + f).toString(), usdtDecimals);
    } catch {
      return usdtAvailable;
    }
  })();

  const usdtRestricted = usdt?.restricted === true;
  const wattBalanceUsdt = humanTimesRate(wattBalance, jouleUsdtPrice);

  const platformAsset = (symbol: string): Asset => ({
    chain_id: 0,
    token_symbol: symbol,
    token_address: null,
    balance: wattBalance,
    frozen_balance: wattFrozen,
    available_balance: wattAvailable,
    balance_usdt: wattBalanceUsdt,
    icon: '',
    current_price: jouleUsdtPrice,
    price_change_24h: 0,
  });

  const usdtAsset: Asset = {
    chain_id: 0,
    token_symbol: 'USDT',
    token_address: null,
    balance: usdtBalance,
    frozen_balance: usdtFrozen,
    available_balance: usdtAvailable,
    balance_usdt: usdtAvailable,
    icon: '',
    current_price: 1,
    price_change_24h: 0,
    restricted: usdtRestricted,
  };

  // 展示用合计：平台币 × 汇率 + USDT；用十进制字符串相加
  const platformTotal = safeAdd(wattAvailable, wattFrozen);
  const platformTotalUsdt = humanTimesRate(platformTotal, jouleUsdtPrice);
  const usdtTotal = safeAdd(usdtAvailable, usdtFrozen);
  const totalUsdt = safeAdd(platformTotalUsdt, usdtTotal);

  return {
    total_balance_usdt: totalUsdt,
    usdtRestricted,
    assets: [
      platformAsset('WATT'),
      platformAsset('TAKER'), // alias for legacy TAKER/Joule lookups
      platformAsset('JOULE'),
      usdtAsset,
    ],
  };
};
